import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Eye } from 'lucide-react'
import { PageHeader, Panel, Chip, Button, Select, useToast } from '../ds/index'
import { Icon } from '../components/Icon'
import { useApi } from '../lib/useApi'
import { post, put, del } from '../lib/api'
import { money, title } from '../lib/format'
import type { Subscriber } from '../lib/types'
import { roleName } from '../lib/types'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import { SubscriberForm } from './Subscribers'
import { ClientWallet } from './Balances'
import Disputes from './Disputes'
import { ClientBillingStatus } from './ClientBillingStatus'
import { ClientAgreementRecords } from './Applications'

/**
 * One client company: who they are and where they are placed, their balance and
 * how they buy, their disputes, and a button to see their portal exactly as they
 * do (read-only, whether or not anyone there has a login yet).
 */
export default function ClientPage() {
  const { id } = useParams()
  const { hash } = useLocation()
  const subs = useApi<{ subscribers: Subscriber[] }>('/api/subscribers')
  const [editing, setEditing] = useState(false)
  const [opening, setOpening] = useState(false)
  const toast = useToast()
  const sub = subs.data?.subscribers.find((s) => s.id === id)

  useEffect(() => {
    if (hash === '#disputes' && sub) document.getElementById('disputes')?.scrollIntoView({ block: 'start' })
  }, [hash, sub])

  if (subs.error) return <ErrorBlock error={subs.error} />
  if (!subs.data) return <LoadingBlock />
  if (!sub) return <Panel><p style={{ margin: 0 }}>No client with that link. <Link to="/clients">All clients</Link></p></Panel>

  const openPortal = async () => {
    setOpening(true)
    try {
      await post(`/api/clients/${sub.id}/preview`, {})
      // The session now shows this client's portal; a full load swaps the whole app over to it.
      window.location.assign('/portal')
    } catch (e) { toast((e as Error).message, 'neg'); setOpening(false) }
  }
  const names = (xs: { name: string }[]) => xs.map((x) => x.name).join(', ')

  return (
    <>
      <PageHeader title={sub.name}
        subtitle={`${sub.category_name}${sub.areas.length ? ` in ${names(sub.areas)}` : ''}. ${title(sub.status)}.`}
        actions={<div className="row-tight">
          <Link className="btn btn-ghost" to="/clients">All clients</Link>
          <Button variant="secondary" onClick={() => setEditing(true)}>Edit details</Button>
          <Button onClick={openPortal} disabled={opening}><Icon of={Eye} /> {opening ? 'Opening' : 'See client portal'}</Button>
        </div>} />

      <div className="stack">
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Company</h2><Chip tone={sub.status === 'active' ? undefined : 'warn'} dot={sub.status !== 'active'}>{title(sub.status)}</Chip></div>
          <dl className="client-facts">
            <div><dt>Website</dt><dd>{sub.website ? <a href={sub.website.startsWith('http') ? sub.website : `https://${sub.website}`} target="_blank" rel="noopener noreferrer">{sub.website.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '')}</a> : <span className="muted">none</span>}</dd></div>
            <div><dt>Rep</dt><dd>{sub.reps.length ? names(sub.reps) : <span className="fact-warn">No rep yet, so no leads</span>}</dd></div>
            <div><dt>Groups (from their rep)</dt><dd>{sub.groups.length ? names(sub.groups) : <span className="fact-warn">{sub.reps.length ? 'Their rep is in no groups yet' : 'None until a rep is assigned'}</span>}</dd></div>
            <div><dt>Price per lead</dt><dd>{sub.price_per_lead_cents != null ? money(sub.price_per_lead_cents) : <span className="muted">by tier</span>}</dd></div>
            <div><dt>Last 30 days</dt><dd>{sub.leads_30d} lead{sub.leads_30d === 1 ? '' : 's'}, {sub.responded_30d} replied, {money(sub.billed_30d_cents || 0)} billed</dd></div>
          </dl>
          {sub.about && <p style={{ marginBottom: 0 }}>{sub.about}</p>}
        </Panel>

        <Panel>
          <div className="panel-header"><h2 className="panel-title">Contacts</h2><Button variant="ghost" onClick={() => setEditing(true)}>Add or edit</Button></div>
          {sub.contacts.length === 0 ? <p className="muted" style={{ margin: 0 }}>No contacts yet.</p> : (
            <div className="contact-rows">
              {sub.contacts.map((c) => (
                <div key={c.id} className="contact-row">
                  <div>
                    <strong>{c.name}</strong> <span className="muted">{roleName(c)}</span>
                    {c.is_primary && <> <Chip tone="accent">Primary</Chip></>}
                    <div className="muted">{[c.email, c.phone].filter(Boolean).join(' · ') || 'no email or phone'}</div>
                  </div>
                  <div className="row-tight" style={{ flexWrap: 'wrap' }}>
                    {c.lead_emails && <Chip>Lead emails</Chip>}
                    {c.billing_emails && <Chip>Billing emails</Chip>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <DiscountPanel clientId={sub.id} discount={sub.discount || null} onChanged={() => subs.reload()} />

        <ClientBillingStatus clientId={sub.id} />
        <ClientAgreementRecords clientId={sub.id} />
        <ClientWallet clientId={sub.id} embedded />

        <div id="disputes"><Disputes clientId={sub.id} embedded /></div>

        <DeleteClient id={sub.id} name={sub.name} />
      </div>

      {editing && <SubscriberForm sub={sub} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); subs.reload() }} />}
    </>
  )
}

/**
 * Delete for a client with no history (a test or a mistake). Anyone with leads,
 * money or statements can only be cancelled, and the panel says why.
 */
function DeleteClient({ id, name }: { id: string; name: string }) {
  const check = useApi<{ deletable: boolean; reason: string | null }>(`/api/clients/${id}/deletable`)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const navigate = useNavigate()
  if (!check.data) return null

  const remove = async () => {
    setBusy(true)
    try {
      await del(`/api/clients/${id}`)
      toast(`${name} is deleted`)
      navigate('/clients')
    } catch (e) { toast((e as Error).message, 'neg'); setBusy(false); setConfirming(false) }
  }

  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">Delete client</h2></div>
      {!check.data.deletable ? <p className="muted" style={{ margin: 0 }}>{check.data.reason}</p>
        : confirming ? (
          <div className="stack">
            <p style={{ margin: 0 }}>Delete {name}, its contacts and its login? This cannot be undone.</p>
            <div className="row-tight">
              <Button onClick={remove} disabled={busy}>{busy ? 'Deleting' : 'Yes, delete'}</Button>
              <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>Keep them</Button>
            </div>
          </div>
        ) : (
          <div className="stack">
            <p className="muted" style={{ margin: 0 }}>No leads, payments or statements yet, so this client can be removed completely.</p>
            <div><Button variant="secondary" onClick={() => setConfirming(true)}>Delete client</Button></div>
          </div>
        )}
    </Panel>
  )
}

/**
 * The client's discount code (migration 043): every lead they are sold is
 * priced after it. Pick a code to put on them, or take it off.
 */
function DiscountPanel({ clientId, discount, onChanged }: { clientId: string; discount: { code: string; percent_off: number; active: boolean } | null; onChanged: () => void }) {
  const codes = useApi<{ codes: { id: string; code: string; percent_off: number; active: boolean; note: string | null }[] }>('/api/discount-codes')
  const [pick, setPick] = useState('')
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const apply = async () => {
    setBusy(true)
    try { await put(`/api/clients/${clientId}/discount`, { code: pick }); toast(`${pick} applied`, 'pos'); setPick(''); onChanged() }
    catch (e) { toast((e as Error).message, 'neg') }
    setBusy(false)
  }
  const remove = async () => {
    setBusy(true)
    try { await del(`/api/clients/${clientId}/discount`); toast('Discount removed'); onChanged() }
    catch (e) { toast((e as Error).message, 'neg') }
    setBusy(false)
  }
  const active = (codes.data?.codes || []).filter((c) => c.active)
  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">Discount</h2>
        {discount && <Chip tone={discount.active ? 'pos' : 'warn'} dot>{discount.code}, {discount.percent_off}% off{discount.active ? '' : ' (turned off)'}</Chip>}
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        {discount ? (discount.active ? `Every lead they are sold is ${discount.percent_off === 100 ? 'free' : `${discount.percent_off}% off`}.` : 'Their code is turned off, so they pay full price.') : 'They pay full price. Put a code on them to discount every lead from now on.'}
      </p>
      <div className="row-tight" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <Select aria-label="Discount code" value={pick} onChange={(e) => setPick(e.target.value)}>
          <option value="">{active.length ? 'Choose a code' : 'No codes yet: make one in Settings, Tiers and pricing'}</option>
          {active.map((c) => <option key={c.id} value={c.code}>{c.code}, {c.percent_off}% off{c.note ? ` (${c.note})` : ''}</option>)}
        </Select>
        <Button variant="secondary" onClick={apply} disabled={!pick || busy}>{discount ? 'Change code' : 'Apply code'}</Button>
        {discount && <Button variant="ghost" onClick={remove} disabled={busy}>Remove</Button>}
      </div>
    </Panel>
  )
}
